import { type FC, useState } from "react"
import { useDropzone } from "react-dropzone"
import styled from "styled-components"

import { removeArrayIndex, replaceArrayValue } from "@forento/core/utilities/array"
import { getDataUrlByFile, getExtensionByFileName, supportedFileExtensions } from "@forento/core/utilities/file"
import { parseNumber } from "@forento/core/utilities/number"
import { getVimeoIdFromUrl, getYouTubeIdFromUrl } from "@forento/core/utilities/url"
import { useFileUrl } from "@forento/ui/hooks/useFileUrl"

import Button, { SubmitButton } from "~/components/Button"
import Checkbox from "~/components/Checkbox"
import Dropdown from "~/components/Dropdown"
import { AddIcon, DeleteIcon, UploadFileIcon, UploadIcon } from "~/components/Icon"
import InputField, { useRichTextArea } from "~/components/InputField"
import InputLabel from "~/components/InputLabel"
import { DefaultModal, ModalButtons, ModalInputs, ModalTitle } from "~/components/Modal"
import { useAlert } from "~/contexts/AlertContext"
import { useUser } from "~/contexts/UserContext"
import trpc, { query } from "~/utilities/trpc"

type Props = { channel: { id: number | null; name: string }; onReloadPosts(): Promise<unknown> }
const CreatePost: FC<Props> = ({ channel, onReloadPosts }) => {
	const user = useUser()
	const alert = useAlert()

	const [isModalOpen, setModalOpen] = useState(false)
	const [title, setTitle] = useState("")
	const content = useRichTextArea({ label: "Text" })
	const [images, setImages] = useState<File[]>([])
	const [videoUrl, setVideoUrl] = useState("")
	const [pollOptions, setPollOptions] = useState<string[]>([])
	const [categoryId, setCategoryId] = useState<string | null>(null)
	const [attachments, setAttachments] = useState<{ label: string; file: File }[]>([])
	const [sendNotifications, setSendNotifications] = useState(false)

	const categories = query.community.listPostCategories.useQuery()

	const createPost = query.community.createPost.useMutation({
		async onSuccess(result) {
			setTitle("")
			content.clear()
			setCategoryId(null)
			setVideoUrl("")
			setPollOptions([])
			setAttachments([])
			setSendNotifications(false)
			await onReloadPosts()
			setModalOpen(false)
			if (result.moderationStatus === "pending")
				void alert.show(
					"Awaiting approval",
					"Your post has been submitted for review and will appear once approved.",
				)
		},
		onError(error) {
			console.error(error)
			alert.show("Error", "Failed to create post. Please try again.")
		},
	})

	const imagesDropzone = useDropzone({
		accept: Object.fromEntries(supportedFileExtensions.image.map(x => [`image/${x.toLowerCase()}`, []])),
		onDrop: files => setImages(current => [...current, ...files]),
	})
	const attachmentsDropzone = useDropzone({
		onDrop: async files => {
			for (const file of files) {
				const dialog = await alert.input(
					"Add attachment",
					`Please enter a label for the file ${file.name}.`,
					"Label",
					file.name,
				)
				if (dialog.result === null) continue
				dialog.close()
				setAttachments(current => [...current, { label: dialog.result, file }])
			}
		},
	})

	const handleCreateCommunityProfile = async () => {
		if (user.user!.communityProfileId !== null) return

		await trpc.community.createProfile.mutate()
		await user.reload()
	}

	const handleCreatePost = async () => {
		const exportedContent = await content.exportEditorState()
		if (title.trim().length === 0 || exportedContent === null) return

		createPost.mutate({
			channelId: channel.id,
			title: title.trim(),
			content: exportedContent,
			imageDataUrls: await Promise.all(images.map(image => getDataUrlByFile(image))),
			youtubeId: getYouTubeIdFromUrl(videoUrl.trim()),
			vimeoId: getVimeoIdFromUrl(videoUrl.trim()),
			categoryId: parseNumber(categoryId),
			pollOptions,
			attachments: await Promise.all(
				attachments.map(async attachment => ({
					label: attachment.label,
					dataUrl: await getDataUrlByFile(attachment.file),
					extension: getExtensionByFileName(attachment.file.name) ?? "",
				})),
			),
			sendNotifications,
		})
	}

	if (user.user!.communityProfileId === null) {
		return (
			<Container>
				<p>
					In order to post and interact with others on the community, you need to create a community profile.
				</p>
				<Button variant="primary" onClick={handleCreateCommunityProfile}>
					Create community profile
				</Button>
			</Container>
		)
	}

	return (
		<Container>
			<Button variant="primary" onClick={() => setModalOpen(true)}>
				Create post
			</Button>
			<DefaultModal
				onClose={() => setModalOpen(false)}
				isOpen={isModalOpen}
				onSubmit={handleCreatePost}
				width={800}
			>
				<ModalTitle>Create a new post</ModalTitle>
				<ModalInputs>
					<InputField label="Title" value={title} onChange={setTitle} />
					{content.element}
					<div>
						<InputLabel>Images</InputLabel>
						<div className="flex flex-wrap gap-4">
							{images.map((image, index) => (
								<UploadedImage
									key={index}
									image={image}
									onDelete={() => setImages(current => removeArrayIndex(current, index))}
								/>
							))}
							<div
								className="border-primary relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed break-all"
								{...imagesDropzone.getRootProps()}
							>
								<input {...imagesDropzone.getInputProps()} />
								<UploadIcon className="text-primary h-6 w-6" />
							</div>
						</div>
					</div>
					<div>
						<InputLabel>Video embed</InputLabel>
						<InputField
							inputType="url"
							placeholder="YouTube or Vimeo URL"
							value={videoUrl}
							onChange={setVideoUrl}
						/>
					</div>
					<div className="border-component-border bg-component-background/40 flex flex-col gap-3 rounded-lg border p-3">
						<div className="flex items-center justify-between gap-3">
							<p className="text-sm font-semibold text-gray-900">Poll options</p>
							{pollOptions.length > 0 && (
								<p className="text-light shrink-0 text-xs font-medium">
									{pollOptions.length} {pollOptions.length === 1 ? "option" : "options"}
								</p>
							)}
						</div>
						{pollOptions.map((option, index) => (
							<div
								key={index}
								className="border-component-border flex items-center gap-2 rounded-lg border bg-white p-2 shadow-sm"
							>
								<span className="bg-primary/10 text-primary flex size-7 shrink-0 items-center justify-center rounded-full text-xs font-semibold">
									{index + 1}
								</span>
								<div className="min-w-0 flex-1">
									<InputField
										value={option}
										placeholder="Poll option..."
										onChange={value =>
											setPollOptions(current => replaceArrayValue(current, index, value))
										}
										compact
									/>
								</div>
								<button
									type="button"
									aria-label={`Delete poll option ${index + 1}`}
									title="Delete poll option"
									className="border-danger/20 text-danger hover:bg-danger/10 flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-lg border bg-white transition-colors"
									onClick={() => setPollOptions(current => removeArrayIndex(current, index))}
								>
									<DeleteIcon className="size-4" />
								</button>
							</div>
						))}
						<Button
							variant="secondary"
							className="w-full"
							onClick={() => setPollOptions(current => [...current, ""])}
						>
							<AddIcon className="size-5" /> Add poll option
						</Button>
					</div>
					<Dropdown
						label="Category"
						items={[
							{ id: "none", title: "-- No category --" },
							...(categories.data?.map(category => ({
								id: category.id.toString(),
								title: category.label,
							})) ?? []),
							{ id: "create", title: "Create new category" },
						]}
						selectedItemId={categoryId ?? "none"}
						onChange={async id => {
							if (id === "create") {
								const result = await alert.input(
									"Create category",
									"Please enter a name for the category.",
									"Name",
								)
								if (result.result === null) return
								const category = await trpc.community.createPostCategory.mutate(result.result.trim())
								await categories.refetch()
								result.close()
								setCategoryId(category.id.toString())
								return
							}

							setCategoryId(id === "none" ? null : id)
						}}
					/>
					<div>
						<InputLabel>Attachments</InputLabel>
						<div className="flex flex-wrap gap-4">
							{attachments.map((attachment, index) => (
								<div
									key={index}
									className="border-primary relative flex h-24 w-24 items-center justify-center rounded-lg border border-dashed p-2 break-all"
								>
									<Button
										className="absolute top-1 right-1 rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
										onClick={() => setAttachments(current => removeArrayIndex(current, index))}
									>
										<DeleteIcon className="text-danger h-4 w-4" />
									</Button>
									{attachment.label}
								</div>
							))}
							<div
								className="border-primary relative flex h-24 w-24 cursor-pointer items-center justify-center rounded-lg border border-dashed break-all"
								{...attachmentsDropzone.getRootProps()}
							>
								<input {...attachmentsDropzone.getInputProps()} />
								<UploadFileIcon className="text-primary h-6 w-6" />
							</div>
						</div>
					</div>
					<Checkbox
						label="Notify students of post"
						isChecked={sendNotifications}
						onChange={setSendNotifications}
					/>
				</ModalInputs>
				<ModalButtons>
					<Button variant="secondary" isDisabled={createPost.isPending} onClick={() => setModalOpen(false)}>
						Close
					</Button>
					<SubmitButton variant="primary" isLoading={createPost.isPending}>
						Post to {channel.name}
					</SubmitButton>
				</ModalButtons>
			</DefaultModal>
		</Container>
	)
}

const UploadedImage: FC<{ image: File; onDelete(): void }> = ({ image, onDelete }) => {
	const imageUrl = useFileUrl(image)

	return (
		<div className="relative">
			<img src={imageUrl} className="h-24 rounded-lg" alt={image.name} />
			<button
				type="button"
				className="absolute top-2 right-2 cursor-pointer rounded-full bg-black/50 p-2 transition-all duration-100 hover:bg-black/70"
				onClick={onDelete}
			>
				<DeleteIcon className="text-danger h-4 w-4" />
			</button>
		</div>
	)
}

const Container = styled.div`
	grid-area: create-post;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 16px;
`

export default CreatePost
